import { useState, useCallback } from 'react';
import { makeStyles, Button, TextField } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import { spacing20, spacing30, colorGlobalBorderDefault, spacing10 } from '@ellucian/react-design-system/core/styles/tokens';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@ellucian/ds-icons/lib';

const useStyles = makeStyles({
    name: 'ApplicantMessageInput'
})(() => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing20,
        marginTop: spacing30,
        paddingTop: spacing30,
        borderTop: `1px solid ${colorGlobalBorderDefault}` },
    inputWrapper: {
        flex: 1 },
    sendButton: {
        flexShrink: 0 },
    buttonIcon: {
        marginRight: spacing10 } }));

const ApplicantMessageInput = ({ questionId, onSendMessage, disabled = false }) => {
    const { classes } = useStyles();
    const intl = useIntl();
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    const handleSend = useCallback(async () => {
        if (!message.trim() || isSending) return;
        
        setIsSending(true);
        try {
            await onSendMessage(questionId, message.trim());
            setMessage('');
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setIsSending(false);
        }
    }, [message, questionId, onSendMessage, isSending]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    return (
        <div className={classes.container}>
            <div className={classes.inputWrapper}>
                <TextField
                    fullWidth
                    placeholder={intl.formatMessage({ id: 'applicantMessagePlaceholder' })}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || isSending}
                    label={intl.formatMessage({ id: 'applicantMessageLabel' })}
                />
            </div>
            <Button
                className={classes.sendButton}
                onClick={handleSend}
                disabled={disabled || isSending || !message.trim()}
                aria-label={intl.formatMessage({ id: 'send' })}
                size="large"
            >
                <Icon name="send" className={classes.buttonIcon} />
                <FormattedMessage id="send" />
            </Button>
        </div>
    );
};

export default ApplicantMessageInput;
